import { useEffect, useRef, useState } from 'react'
import { extractImeiCandidates, isValidImei, mergeCandidates, type ImeiCandidate } from '@/lib/imei'

interface Props {
  open: boolean
  onClose: () => void
  /** Called when an IMEI is accepted (barcode auto-fill or confirmed OCR candidate). */
  onFill: (imei: string) => void
  targetLabel: string
}

/**
 * Fast IMEI scanner using barcode-detector (ZXing-C++ via WASM), which reads partial,
 * tilted and low-contrast codes far better than the old pure-JS ZXing port — so the
 * barcode is picked up as soon as it is roughly in view, not only when fully framed.
 *
 * Behaviour the owner asked for on real device:
 * - High-res rear camera with continuous autofocus.
 * - On a successful read: haptic tick, fill the target field, and AUTO-CLOSE the camera
 *   so it is obvious the scan worked. Re-open via the field's scan icon for IMEI 2.
 * - OCR (Tesseract, lazy) remains a confirmation-only fallback for IMEI screens.
 */
export function ImeiScanner({ open, onClose, onFill, targetLabel }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<number | null>(null)
  const lastHitRef = useRef<{ value: string; at: number }>({ value: '', at: 0 })
  const [status, setStatus] = useState<'starting' | 'scanning' | 'denied'>('starting')
  const [candidates, setCandidates] = useState<ImeiCandidate[]>([])
  const [ocrBusy, setOcrBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  function stopCamera() {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const video = videoRef.current
    if (!video) return

    setStatus('starting')
    setCandidates([])
    setMsg(null)

    ;(async () => {
      try {
        const { BarcodeDetector } = await import('barcode-detector')
        const detector = new BarcodeDetector({
          formats: ['code_128', 'code_39', 'ean_13', 'itf', 'codabar', 'qr_code'],
        })
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        video.srcObject = stream
        await video.play()

        // Continuous autofocus so close-up box/screen barcodes stay sharp.
        const track = stream.getVideoTracks()[0]
        const caps = track.getCapabilities?.() as MediaTrackCapabilities & { focusMode?: string[] }
        if (caps?.focusMode?.includes('continuous')) {
          await track
            .applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] })
            .catch(() => undefined)
        }

        setStatus('scanning')
        timerRef.current = window.setInterval(() => {
          if (cancelled) return
          void detector
            .detect(video)
            .then((codes) => {
              if (cancelled) return
              for (const code of codes) {
                const found = extractImeiCandidates(code.rawValue ?? '')
                const auto = found.find((c) => c.confidence === 'high' || c.confidence === 'medium')
                if (auto) {
                  const now = Date.now()
                  if (lastHitRef.current.value === auto.value && now - lastHitRef.current.at < 1200) return
                  lastHitRef.current = { value: auto.value, at: now }
                  navigator.vibrate?.(80)
                  onFill(auto.value)
                  onClose() // auto-close so the user knows it scanned
                  return
                }
                if (found.length > 0) setCandidates((prev) => mergeCandidates(prev, found))
              }
            })
            .catch(() => undefined)
        }, 120)
      } catch {
        if (!cancelled) setStatus('denied')
      }
    })()

    return () => {
      cancelled = true
      stopCamera()
    }
  }, [open, onFill, onClose])

  async function captureOcr() {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    setOcrBusy(true)
    setMsg(null)
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('no canvas context')
      ctx.drawImage(video, 0, 0)
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!blob) throw new Error('frame capture failed')
      const Tesseract = (await import('tesseract.js')).default
      const { data } = await Tesseract.recognize(blob, 'eng')
      const found = extractImeiCandidates(data.text ?? '')
      if (found.length) setCandidates((prev) => mergeCandidates(prev, found))
      else setMsg('No IMEI digits read. Improve focus/light, or enter manually.')
    } catch (e) {
      setMsg(`OCR failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setOcrBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 backdrop-blur" role="dialog" aria-modal="true" aria-label="IMEI scanner">
      <div className="flex items-center justify-between px-4 py-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <div>
          <h2 className="text-base font-semibold text-slate-100">Scan IMEI</h2>
          <p className="text-[11px] text-slate-400">Filling: {targetLabel}</p>
          {targetLabel === 'IMEI 2' ? (
            <p className="text-[11px] text-slate-500">Optional — tap Done if this phone has one IMEI.</p>
          ) : null}
        </div>
        <button type="button" onClick={onClose} className="rounded-lg bg-slate-700 px-4 py-1.5 text-sm font-semibold text-slate-100">
          Done
        </button>
      </div>

      <div className="relative mx-auto aspect-square w-full max-w-md overflow-hidden rounded-xl">
        <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="relative h-40 w-64">
            <span className="absolute -left-1 -top-1 h-6 w-6 border-l-4 border-t-4 border-sky-400" />
            <span className="absolute -right-1 -top-1 h-6 w-6 border-r-4 border-t-4 border-sky-400" />
            <span className="absolute -bottom-1 -left-1 h-6 w-6 border-b-4 border-l-4 border-sky-400" />
            <span className="absolute -bottom-1 -right-1 h-6 w-6 border-b-4 border-r-4 border-sky-400" />
          </div>
        </div>
        {status === 'starting' ? <Overlay>Starting camera…</Overlay> : null}
        {status === 'denied' ? (
          <Overlay>Camera permission denied. Allow camera in Settings, or close and type the IMEI.</Overlay>
        ) : null}
        {status === 'scanning' ? (
          <p className="pointer-events-none absolute inset-x-0 bottom-2 text-center text-[11px] text-slate-200/90">
            Barcode ko frame ke qareeb lao — partial bhi chal jaayega
          </p>
        ) : null}
      </div>

      <div className="mx-auto w-full max-w-md space-y-3 px-4 pb-6 pt-3" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
        {msg ? <p className="text-center text-xs text-amber-300">{msg}</p> : null}

        {candidates.length > 0 ? (
          <div>
            <p className="mb-1 text-xs text-slate-400">Low-confidence reads — tap to confirm:</p>
            <ul className="space-y-2">
              {candidates.slice(0, 4).map((c) => (
                <li key={c.value}>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.vibrate?.(80)
                      onFill(c.value)
                      onClose()
                    }}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-left"
                  >
                    <span className="font-mono text-sm tabular-nums text-slate-100">{c.value}</span>
                    <span className="text-xs text-amber-400">
                      {c.confidence === 'low' ? 'checksum failed' : 'check digit missing'}
                      {isValidImei(c.value) ? '' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => void captureOcr()}
          disabled={ocrBusy || status !== 'scanning'}
          className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-300 disabled:opacity-60"
        >
          {ocrBusy ? 'Reading IMEI screen…' : 'Barcode not reading? Read IMEI screen (OCR)'}
        </button>
      </div>
    </div>
  )
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/70 px-6 text-center text-sm text-slate-300">
      {children}
    </div>
  )
}
